// Seeded PRNG for v2 lobby. All match-time randomness — AI tavern,
// pairing, opponent generation, AI hero pick — flows through one of
// these so a given seed produces a deterministic match.
//
// Algorithm: mulberry32. Tiny, fast, well-known, good-enough quality
// for game state. Single 32-bit unsigned state field.

export interface RNG {
  /** Returns a float in [0, 1). */
  next(): number;
  /** Returns an integer in [0, maxExclusive). */
  int(maxExclusive: number): number;
  /** Returns a uniformly-chosen element of `arr`. Throws on empty. */
  pick<T>(arr: readonly T[]): T;
  /** Returns a new array — Fisher-Yates shuffle, non-mutating. */
  shuffle<T>(arr: readonly T[]): T[];
  /** Current internal state (for snapshot/replay). */
  state(): number;
}

export function mulberry32(seed: number): RNG {
  let s = seed >>> 0;

  function next(): number {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  function int(maxExclusive: number): number {
    if (maxExclusive <= 0) return 0;
    return Math.floor(next() * maxExclusive);
  }

  function pick<T>(arr: readonly T[]): T {
    if (arr.length === 0) {
      throw new Error('rng.pick called on empty array');
    }
    return arr[int(arr.length)];
  }

  function shuffle<T>(arr: readonly T[]): T[] {
    const out = [...arr];
    for (let i = out.length - 1; i > 0; i--) {
      const j = int(i + 1);
      [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
  }

  return { next, int, pick, shuffle, state: () => s };
}

/** Generate a seed from current time + a sprinkle of entropy. */
export function freshSeed(): number {
  // Math.random() is fine here — we only need the seed itself to be
  // unpredictable; the match's randomness from then on is fully seeded.
  return ((Date.now() & 0xffffffff) ^ Math.floor(Math.random() * 0xffffffff)) >>> 0;
}
